import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, CheckCircle, Star, Target, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interestOptions = [
  "Career Development",
  "Education & College Prep",
  "Financial Literacy",
  "Leadership Skills",
  "Communication Skills",
  "Personal Development",
  "Health & Wellness",
  "Arts & Creativity",
  "Technology & Digital Skills",
  "Community Service",
  "We can talk about it"
];

const backgroundOptions = [
  "Foster Youth",
  "Justice Involved",
  "Low Income",
  "At Risk"
];

const MentorshipForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    age: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    school_or_occupation: "",
    background: [],
    dreams: "",
    goals: "",
    success_definition: "",
    why_mentorship: "",
    interests: [],
    heard_about_us: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleBackgroundToggle = (bg) => {
    setFormData(prev => ({
      ...prev,
      background: prev.background.includes(bg)
        ? prev.background.filter(b => b !== bg)
        : [...prev.background, bg]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        age: parseInt(formData.age)
      };

      await axios.post(`${API}/mentorship`, submitData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20 bg-white" data-testid="mentorship-success">
        <div className="card-elevated max-w-lg mx-auto rounded-3xl p-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-soar-teal/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-soar-teal" />
          </div>
          <h2 className="font-heading text-3xl text-soar-blue mb-4 tracking-tight">
            Application Submitted!
          </h2>
          <p className="text-soar-blue/60 mb-6">
            Thank you for applying for our Mentorship Program! We'll review your application
            and reach out to you soon about next steps.
          </p>
          <p className="font-accent text-3xl text-soar-gold">
            Your journey to soar begins here!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="mentorship-form-page" className="bg-white">

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/lvylvkys_fresh%20start%20mentor%20participant.png"
            alt="Mentorship"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/95 to-soar-blue/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
              Mentorship Program
            </span>
            <h1 className="font-heading text-6xl sm:text-7xl leading-none tracking-tight text-soar-blue opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Apply for <span className="text-soar-gold">Mentorship</span>
            </h1>
            <p className="text-xl text-soar-blue/60 opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              Join our mentorship program and get paired with an experienced mentor who will
              guide you towards achieving your dreams and goals.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4 opacity-0 animate-fade-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
              <div className="text-center p-4 card-elevated rounded-2xl">
                <Star className="w-6 h-6 text-soar-gold mx-auto mb-2" aria-hidden="true" />
                <p className="text-xs text-soar-blue/60 font-medium">Personal Growth</p>
              </div>
              <div className="text-center p-4 card-elevated rounded-2xl">
                <Target className="w-6 h-6 text-soar-gold mx-auto mb-2" aria-hidden="true" />
                <p className="text-xs text-soar-blue/60 font-medium">Goal Setting</p>
              </div>
              <div className="text-center p-4 card-elevated rounded-2xl">
                <Users className="w-6 h-6 text-soar-gold mx-auto mb-2" aria-hidden="true" />
                <p className="text-xs text-soar-blue/60 font-medium">1-on-1 Support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-5" style={{ backgroundColor: '#212163' }}>
        <Marquee speed={50} gradient={false}>
          {["THINK", "BUILD", "DREAM", "GROW", "SOAR", "LEAD"].map((w) => (
            <span key={w} className="font-heading text-4xl mx-12 tracking-wider" style={{ color: 'rgba(221,182,77,0.3)' }}>{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Application Form */}
      <section className="py-20" data-testid="mentorship-form-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="card-elevated rounded-3xl p-8 sm:p-10">
            <h2 className="font-heading text-3xl text-soar-blue mb-2 tracking-tight">
              Mentorship Application Form
            </h2>
            <p className="text-soar-blue/50 mb-8">
              Fill out the form below to apply. All fields marked with * are required.
            </p>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Personal Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-soar-blue/70">First Name *</Label>
                    <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-soar-blue/70">Last Name *</Label>
                    <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-last-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-soar-blue/70">Email *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-soar-blue/70">Phone *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth" className="text-soar-blue/70">Date of Birth *</Label>
                    <Input id="date_of_birth" name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="mentorship-dob" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-soar-blue/70">Age *</Label>
                    <Input id="age" name="age" type="number" min="13" max="30" value={formData.age} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-age" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="school_or_occupation" className="text-soar-blue/70">School/Occupation *</Label>
                    <Input id="school_or_occupation" name="school_or_occupation" value={formData.school_or_occupation} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" placeholder="e.g., High School Student, College Freshman" data-testid="mentorship-occupation" />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Address</h3>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-soar-blue/70">Street Address</Label>
                    <Input id="address" name="address" value={formData.address} onChange={handleChange} className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-address" />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-soar-blue/70">City *</Label>
                      <Input id="city" name="city" value={formData.city} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-city" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state" className="text-soar-blue/70">State *</Label>
                      <Input id="state" name="state" value={formData.state} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-state" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip_code" className="text-soar-blue/70">ZIP Code *</Label>
                      <Input id="zip_code" name="zip_code" value={formData.zip_code} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" data-testid="mentorship-zip" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Background */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Background</h3>
                <p className="text-sm text-soar-blue/50 mb-4">Select any that apply (optional):</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {backgroundOptions.map((bg) => (
                    <label key={bg} htmlFor={`bg-${bg}`} className="flex items-center space-x-3 p-3 rounded-xl border border-soar-blue/10 hover:border-soar-blue/20 cursor-pointer bg-soar-blue/5 transition-colors">
                      <input type="checkbox" id={`bg-${bg}`} checked={formData.background.includes(bg)} onChange={() => handleBackgroundToggle(bg)} className="h-4 w-4 rounded border-white/30 text-soar-teal focus:ring-soar-teal bg-soar-blue/10" data-testid={`background-${bg.toLowerCase().replace(/\s+/g, '-')}`} />
                      <span className="text-sm text-soar-blue/80">{bg}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Dreams & Goals */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Your Dreams & Goals</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="dreams" className="text-soar-blue/70">What are your dreams? *</Label>
                    <Textarea id="dreams" name="dreams" value={formData.dreams} onChange={handleChange} required className="rounded-xl min-h-[100px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" placeholder="Tell us about your biggest dreams and aspirations..." data-testid="mentorship-dreams" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="goals" className="text-soar-blue/70">What are your short-term goals? *</Label>
                    <Textarea id="goals" name="goals" value={formData.goals} onChange={handleChange} required className="rounded-xl min-h-[100px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" placeholder="What do you want to achieve in the next 1-2 years?" data-testid="mentorship-goals" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="success_definition" className="text-soar-blue/70">What does success look like to you? *</Label>
                    <Textarea id="success_definition" name="success_definition" value={formData.success_definition} onChange={handleChange} required className="rounded-xl min-h-[100px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" placeholder="Describe what a successful future looks like for you..." data-testid="mentorship-success" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="why_mentorship" className="text-soar-blue/70">Why do you want a mentor? *</Label>
                    <Textarea id="why_mentorship" name="why_mentorship" value={formData.why_mentorship} onChange={handleChange} required className="rounded-xl min-h-[100px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" placeholder="What do you hope to gain from having a mentor?" data-testid="mentorship-why" />
                  </div>
                </div>
              </div>

              {/* Areas of Interest */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Areas of Interest</h3>
                <p className="text-sm text-soar-blue/50 mb-4">Select the areas you'd like to focus on with your mentor:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {interestOptions.map((interest) => (
                    <label key={interest} htmlFor={`interest-${interest}`} className="flex items-center space-x-3 p-3 rounded-xl border border-soar-blue/10 hover:border-soar-blue/20 cursor-pointer bg-soar-blue/5 transition-colors">
                      <input type="checkbox" id={`interest-${interest}`} checked={formData.interests.includes(interest)} onChange={() => handleInterestToggle(interest)} className="h-4 w-4 rounded border-white/30 text-soar-teal focus:ring-soar-teal bg-soar-blue/10" data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-')}`} />
                      <span className="text-sm text-soar-blue/80">{interest}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* How did you hear about us */}
              <div className="space-y-2">
                <Label htmlFor="heard_about_us" className="text-soar-blue/70">How did you hear about us? *</Label>
                <Select value={formData.heard_about_us} onValueChange={(value) => handleSelectChange("heard_about_us", value)}>
                  <SelectTrigger className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="mentorship-heard-about">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="bg-soar-blue border-soar-blue/10">
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="friend_family">Friend or Family</SelectItem>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="community_event">Community Event</SelectItem>
                    <SelectItem value="website">Website Search</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" disabled={submitting} className="w-full rounded-full bg-soar-teal hover:bg-soar-teal/90 py-6 text-lg font-bold hover:scale-[1.02] transition-transform duration-200" data-testid="mentorship-submit-btn">
                {submitting ? "Submitting..." : (
                  <>
                    Submit Application
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MentorshipForm;
